import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { WELCOME_TIMING, WelcomeTransition } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The welcome transition's host — above the switch from /welcome to the app. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **IT LIVES HERE BECAUSE THE WELCOME SCREEN DOES NOT SURVIVE ITS OWN SUCCESS.** Creating the
 * workspace ends in `session.reload()`, and the moment that fills a workspace in, `Gate` stops
 * rendering `/welcome` and renders the app — unmounting the screen that would have played this.
 * So the overlay is mounted from above `Gate`, and outlives the swap it covers.
 *
 * ⚠️ **`play()` resolves when the screen is WHITE**, not when the overlay ends: the caller reloads
 * then, so the dashboard mounts under the white unseen and is ready when it lifts.
 *
 * ⚠️ **Reduced motion plays nothing** and resolves at once — two and a half seconds of white, still, would be
 * worse than no transition at all.
 *
 * ⚠️ **Outside this provider, `play()` resolves at once**, so a screen rendered on its own (the
 * welcome screen's own suite) behaves exactly as it did before the transition existed.
 */

interface WelcomeTransitionHandle {
  readonly play: () => Promise<void>;
}

const Context = createContext<WelcomeTransitionHandle>({ play: () => Promise.resolve() });

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function WelcomeTransitionProvider({ children }: { readonly children: ReactNode }) {
  const [playing, setPlaying] = useState(false);

  const play = useCallback((): Promise<void> => {
    if (prefersReducedMotion()) return Promise.resolve();
    setPlaying(true);
    return new Promise((resolve) => {
      setTimeout(resolve, WELCOME_TIMING.whiteMs);
    });
  }, []);

  const value = useMemo(() => ({ play }), [play]);
  const stop = useCallback(() => {
    setPlaying(false);
  }, []);

  return (
    <Context.Provider value={value}>
      {children}
      {playing && <WelcomeTransition onDone={stop} />}
    </Context.Provider>
  );
}

export function useWelcomeTransition(): WelcomeTransitionHandle {
  return useContext(Context);
}
